import React from 'react';
import { Box, Container, Typography, Chip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { FileDownload as FileDownloadIcon } from '@mui/icons-material';
import BackButton from '../components/ui/BackButton';
import { useAuth } from '../context/AuthContext';
import DataExportForm from '../components/admin/DataExportForm';
import { isAdmin } from '../utils/permissions';

const PageContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  backgroundColor: theme.palette.mode === 'dark' ? '#121212' : '#f5f5f5',
  padding: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(3),
  },
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(4),
  },
}));

const ContentContainer = styled(Container)(({ theme }) => ({
  maxWidth: '1200px',
  margin: '0 auto',
}));

const HeaderSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    marginBottom: theme.spacing(3),
  },
  [theme.breakpoints.up('md')]: {
    marginBottom: theme.spacing(4),
  },
}));

function DataExportPage() {
  const { userRole } = useAuth();

  // Check if user has admin access
  if (!isAdmin(userRole)) {
    return (
      <PageContainer>
        <ContentContainer>
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              minHeight: '60vh',
              backgroundColor: 'background.paper',
              borderRadius: 2,
              p: { xs: 2, sm: 3, md: 4 },
            }}
          >
            <Typography 
              variant="h5" 
              gutterBottom 
              color="error"
              sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' } }}
            >
              Access Denied
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary" 
              textAlign="center"
              sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
            >
              You don't have permission to access the data export feature. 
              This page is only available to administrators.
            </Typography>
          </Box>
        </ContentContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <ContentContainer>
        <HeaderSection>
          <Box sx={{ 
            display: 'block',
            width: '100%',
            mb: { xs: 1.5, sm: 2 } 
          }}>
            <BackButton to="/admin" label="Back to Dashboard" />
          </Box>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' }, 
            gap: { xs: 1.5, sm: 2 },
            mb: { xs: 1, sm: 0 },
            width: '100%',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 } }}>
              <FileDownloadIcon sx={{ 
                color: '#3498db', 
                fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } 
              }} />
              <Typography 
                variant="h4" 
                component="h1" 
                fontWeight={600}
                sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2.125rem' } }}
              >
                Data Export
              </Typography>
            </Box>
            {userRole === 'super_admin' && (
              <Chip 
                label="Super Admin" 
                color="secondary" 
                size="small"
                sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
              />
            )}
          </Box>
          <Typography 
            variant="body1" 
            color="text.secondary" 
            sx={{ 
              mt: { xs: 0.5, sm: 1 },
              fontSize: { xs: '0.875rem', sm: '1rem' },
            }}
          >
            Export data from the system as CSV files
          </Typography>
        </HeaderSection>

        <DataExportForm />
      </ContentContainer>
    </PageContainer>
  );
}

export default DataExportPage;
